// src/utils/companies.tsx
import React from 'react';
import hevBg from '../assets/images/hevBg.jpg';
import BedriftsAkedamiet from '../assets/images/bedriftsakedamiet.png';
import Fjernvarme from '../assets/images/fjernvarme.png';
import Aider from '../assets/images/aider.png';

export type CompanyData = {
  name: string;
  floor: string;
  logo?: string;
  icon?: string;
  svg?: React.ReactNode;
  bgImage?: string;
  profileImage?: string;
  imageStyle?: React.CSSProperties;
  title?: string;
  description?: string;
  category?: string;
  website?: string;
  profileTitle?: string;
  profileSummary?: string;
  profilePoints?: string[];
  accentColor?: string;
  titleColor?: string;
  descColor?: string;
  phone?: string;
  email?: string;
  hours?: string;
  showInSlide?: boolean;
  overviewText?: string; // short text shown in floor overview
  overviewStyle?: React.CSSProperties; // allows per-company style (color, size, etc.)
};

// Inline SVG for Aider
const AiderSVG: JSX.Element = (
  <svg
    aria-hidden="true"
    className="w-[4.75rem] h-[1.188rem] lg:w-[7rem] lg:h-[1.75rem] text-light-blue-500"
    viewBox="0 0 97 25"
    xmlns="http://www.w3.org/2000/svg"
    width="97"
    height="25"
    fill="none"
  >
    <path
      fill="currentColor"
      d="M25.514 24.586V.494h4.107v24.092h-4.107ZM41.944 24.823c-2.607 0-4.503-.158-6.951-.395V.731c2.37-.237 4.344-.395 6.714-.395 9.558 0 14.218 5.53 14.218 12.006.08 6.794-4.423 12.48-13.981 12.48Zm.316-20.854c-1.185 0-2.212.08-3.08.237v16.825c1.026.158 1.895.237 3.475.237 6.161 0 9.005-3.712 9.005-8.847-.08-5.055-3.318-8.452-9.4-8.452ZM59.56 24.586V.494H74.25v3.713H63.746v6.319h9.557v3.633h-9.557v6.715H74.33v3.712H59.56ZM90.523 14.95c3.001-1.344 4.897-3.95 4.897-7.031 0-4.265-3.002-7.583-9.874-7.583a49.16 49.16 0 0 0-6.319.395v23.855h4.108l.079-13.27 9.004 13.27H97l-6.477-9.637Zm-1.975-2.923-5.292-7.82c.79-.159 1.816-.238 2.764-.238 3.16 0 4.977 1.66 4.977 4.108 0 .948-.158 1.817-.711 2.528-.553.869-1.738 1.421-1.738 1.421ZM11.69.178h-.631L0 24.586h4.186l10.27-9.48.315.712 1.343 3.238 2.37 5.53h4.344L11.691.178Zm-4.423 17.14.632-1.5 3.397-8.057 1.658 3.791-5.687 5.767Z"
    />
  </svg>
);

// ---- Single source of truth: ALL companies live here ----
export const companyPagesData: CompanyData[] = [
  // 1. ETASJE
  {
    name: 'Statens Barnehus',
    floor: '1. etasje',
    icon: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSKanU-1nJw_o4WGl7W2izYJ-izZBy-HYO2uA&s',
    logo: 'https://www.statensbarnehus.no/images/logo.svg',
    website: 'https://www.statensbarnehus.no/',
    category: 'Barn, ungdom og sårbare voksne',
    profileImage:
      'https://www.nkvts.no/content/uploads/2021/06/Barnehusillustrasjon.jpg',
    showInSlide: true, // full slide
    imageStyle: {
      background:
        "linear-gradient(rgba(0,0,0,.8),rgba(0,0,0,.8)), url('https://www.nkvts.no/content/uploads/2021/06/Barnehusillustrasjon.jpg')",
      backgroundSize: 'cover',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      display: 'flex',
      padding: 20,
      justifyContent: 'center',
      flexDirection: 'row',
    },
    title: 'Velkommen til Statens barnehus',
    description:
      'Statens barnehus er et tilbud til barn, ungdom og sårbare voksne som kan ha vært utsatt for, eller vært vitne til vold eller seksuelle overgrep, der det foreligger en politianmeldelse.',
    profileTitle: 'Tverrfaglig kompetansesenter',
    profileSummary:
      'Statens barnehus tilrettelegger for avhør, helseundersøkelser og oppfølging når barn, unge eller særlig sårbare voksne kan ha vært utsatt for vold eller overgrep.',
    profilePoints: [
      'Trygge rammer for avhør',
      'Helseundersøkelser og oppfølging',
      'Tverrfaglig samarbeid i politidistriktene',
    ],
    accentColor: '#8a949c',
    titleColor: '#8a949c ',
    descColor: '#fff',
    phone: '02800',
  },
  {
    name: 'Aider',
    floor: '1. etasje',
    svg: AiderSVG,
    website: 'https://aider.no/',
    category: 'Regnskap, rådgivning og teknologi',
    profileImage: Aider,
    showInSlide: true, // full slide
    imageStyle: {
      background: `linear-gradient(rgba(0,0,0,.7), rgba(0,0,0,.7)), url(${Aider})`,
      backgroundSize: 'cover',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      display: 'flex',
      padding: 20,
      justifyContent: 'center',
      flexDirection: 'row',
    },
    title: 'Aider regnskap, rådgivning og teknologi.',
    description:
      'Aider tilbyr morgendagens tjenester innen regnskap, rådgivning og teknologi. Vi tilbyr skybaserte, skalerbare løsninger tilpasset din virksomhets behov.',
    profileTitle: 'Forretningstjenester samlet på ett sted',
    profileSummary:
      'Aider kombinerer regnskap, lønn, rådgivning, outsourcing og moderne teknologiløsninger for virksomheter som ønsker bedre kontroll og mer effektive prosesser.',
    profilePoints: [
      'Regnskap og lønn',
      'Rådgivning, legal og outsourcing',
      'Teknologi, data og systemløsninger',
    ],
    accentColor: '#108299',
    titleColor: '#108299',
    descColor: '#fff',
    phone: '62 82 76 22',
    email: 'post@aider.no',
    hours: 'Man - Fre, 08:00 - 16:00',
  },
  {
    name: 'NOK Nordland',
    floor: '1. etasje',
    logo: 'https://noknordland.no/wp-content/uploads/sites/10/2020/08/Nok.-Nordland.png',
    website: 'https://noknordland.no/',
    category: 'Lavterskel hjelpetilbud',
    profileImage:
      'https://noknordland.no/wp-content/uploads/sites/10/2020/08/kulli-kittus-KQfxVDHGCUg-unsplash-scaled-1.jpg',
    showInSlide: true,
    imageStyle: {
      background:
        "linear-gradient(rgba(0,0,0,.8),rgba(0,0,0,.8)), url('https://noknordland.no/wp-content/uploads/sites/10/2020/08/kulli-kittus-KQfxVDHGCUg-unsplash-scaled-1.jpg')",
      backgroundSize: 'cover',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      display: 'flex',
      padding: 20,
      justifyContent: 'center',
      flexDirection: 'row',
    },
    descColor: '#fff',
    titleColor: '#fff',
    profileTitle: 'Hjelpetilbud og fagressurs',
    profileSummary:
      'Nok Nordland er et gratis og tverrfaglig lavterskeltilbud for utsatte for overgrep, pårørende og fagpersoner som trenger veiledning.',
    profilePoints: [
      'Samtaletilbud for utsatte og pårørende',
      'Forebyggende undervisning',
      'Fagressurs for skole og helsepersonell',
    ],
    accentColor: '#d25141',
    phone: '75 17 30 50',
    email: 'post@noknordland.no',
  },

  // 2. ETASJE
  {
    name: 'Konfliktrådet i Helgeland',
    floor: '2. etasje',
    logo: 'https://konfliktraadet.no/content/uploads/2021/02/Logo.svg',
    website: 'https://konfliktraadet.no/ditt_konfliktrad/konfliktradet-nordland/',
    category: 'Mekling og konflikthåndtering',
    profileImage:
      'https://konfliktraadet.no/content/uploads/2021/11/120A4372-1024x683.jpg',
    showInSlide: true,
    profileTitle: 'Konfliktrådet Nordland',
    profileSummary:
      'Konfliktrådet legger til rette for dialog og møter der parter kan finne gode løsninger etter lovbrudd, konflikter eller andre vanskelige hendelser.',
    profilePoints: [
      'Mekling og gjenopprettende prosesser',
      'Lokale kontorsteder i Nordland',
      'Veiledning om saker og møter',
    ],
    accentColor: '#9b1c31',
    phone: '75 40 65 80',
    email: 'post.nordland@konfliktraadet.no',
  },
  {
    name: 'Ing. Terje Jakobsen AS',
    floor: '2. etasje',
    logo: '',
    showInSlide: false,
  },
  {
    name: 'Mosjøen Fjernvarme',
    floor: '2. etasje',
    logo: Fjernvarme,
    showInSlide: false,
  },
  {
    name: 'Øynor Consult AS',
    floor: '2. etasje',
    logo: '',
    website: 'https://www.facebook.com/oynorconsult/?locale=nb_NO',
    category: 'Vergemål og dokumentbistand',
    showInSlide: true,
    profileTitle: 'Profesjonelle verger på fulltid',
    profileSummary:
      'Øynor Consult tilbyr profesjonelle vergetjenester og praktisk bistand ved blant annet dødsbo, arveoppgjør, dokumenter og avtaler.',
    profilePoints: [
      'Profesjonelle vergetjenester',
      'Opprydding og bistand ved dødsbo',
      'Dokumentbistand og private avtaler',
    ],
    accentColor: '#2f6f73',
  },

  // 3. ETASJE
  {
    name: 'Nord universitet',
    floor: '3. etasje',
    logo: 'https://www.nord.no/themes/custom/nord/logo.svg',
    website: 'https://www.nord.no/',
    category: 'Utdanning og forskning',
    profileImage: 'https://www.nord.no/modules/custom/nord_misc/assets/images/nord.jpg',
    showInSlide: true,
    profileTitle: 'Studier, forskning og samfunnskunnskap',
    profileSummary:
      'Nord universitet tilbyr studieprogrammer, forskning og fleksible utdanningsløp for studenter, arbeidsliv og samarbeidspartnere i regionen.',
    profilePoints: [
      'Studier og videreutdanning',
      'Forskning og fagmiljøer',
      'Regional tilstedeværelse i Nordland',
    ],
    accentColor: '#005eb8',
    phone: '75 51 70 00',
    email: 'post@nord.no',
  },
  {
    name: 'If forsikring Mosjøen',
    floor: '3. etasje',
    logo: 'https://s3-eu-west-1.amazonaws.com/tpd/logos/58c956220000ff00059e8822/0x0.png',
    website: 'https://www.if.no/privat',
    category: 'Forsikring',
    profileImage: 'https://v.imgi.no/jtlaqlg7zt-BOARDLARGE/1500',
    showInSlide: true,
    profileTitle: 'Trygghet for privatpersoner og bedrifter',
    profileSummary:
      'If hjelper kunder med skadeforsikring, rådgivning og raske skadeoppgjør, og tilbyr forsikringer tilpasset ulike livssituasjoner og virksomheter.',
    profilePoints: [
      'Privat- og bedriftsforsikring',
      'Skadeoppgjør og kundeservice',
      'Nordisk forsikringskompetanse',
    ],
    accentColor: '#0054a6',
  },
  {
    name: 'BedriftsAkademiet AS',
    floor: '3. etasje',
    logo: 'https://bedak.no/wp-content/uploads/2017/10/BedriftsakademietLogo250-1-213x27.png',
    website: 'https://bedak.no/',
    category: 'Arbeidsinkludering og karriereveiledning',
    profileImage: BedriftsAkedamiet,
    showInSlide: true, // full slide
    imageStyle: {
      background: `linear-gradient(rgba(0,0,0,.7), rgba(0,0,0,.7)), url(${BedriftsAkedamiet})`,
      backgroundSize: 'cover',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
      display: 'flex',
      padding: 20,
      justifyContent: 'center',
      flexDirection: 'row',
    },
    title: 'Et trygt og inkluderende arbeidsliv',
    description:
      'Over 35 års erfaring med arbeidsinkludering og karriereveiledning',
    profileTitle: 'Får fram det beste i folk og ut i arbeid',
    profileSummary:
      'BedriftsAkademiet jobber med arbeidsinkludering, helsefremmende tiltak og karriereveiledning som styrker mestringstro og arbeidshåp.',
    profilePoints: [
      'Arbeidsinkludering',
      'Karriereveiledning',
      'Helsefremmende oppfølging',
    ],
    accentColor: '#00a7b5',
    titleColor: '#fff',
    descColor: '#fff',
    email: 'post@bedak.no',
  },
  {
    name: 'Rambøll AS',
    floor: '3. etasje',
    logo: 'https://assets-eu-01.kc-usercontent.com:443/7c3778f1-714a-0155-9be8-162f4c282b22/25674e69-0e10-410a-a637-6f7665ccb064/ramboll.svg',
    website: 'https://www.ramboll.com/no-no',
    category: 'Ingeniør, arkitektur og rådgivning',
    profileImage:
      'https://cdn-assets-eu.frontify.com/s3/frontify-enterprise-files-eu/eyJvYXV0aCI6eyJjbGllbnRfaWQiOiJjbGllbnQtdWtjYnZ6Nmxnc21ibXZ4ayJ9LCJwYXRoIjoicmFtYm9sbFwvZmlsZVwvajhNMmlLR2V4NWQ0ZGJickpRNXMudGlmIn0:ramboll:TKufOJa_dlgRD6o_YPfmyCANbByQQTVDA3tVeqHCwkk?width=1024&height=512&crop=fp&fp=0.6450867,0.2849857&format=jpg',
    showInSlide: true,
    profileTitle: 'Bright ideas. Sustainable change.',
    profileSummary:
      'Rambøll er et globalt ingeniør-, arkitektur- og konsulentselskap som utvikler bærekraftige løsninger for byer, samfunn og næringsliv.',
    profilePoints: [
      'Ingeniørarbeid og design',
      'Natur, miljø og bærekraft',
      'Planlegging og prosjektstyring',
    ],
    accentColor: '#00a7b5',
  },
  {
    name: 'Structor Tekniske Systemer AS',
    floor: '3. etasje',
    logo: 'https://structor.no/wp-content/uploads/2024/05/structor-logo.svg',
    website: 'https://structor.no/selskaper/structor-tekniske-systemer/',
    category: 'Tekniske systemer',
    profileImage:
      'https://structor.no/wp-content/uploads/2024/05/Skjermbilde-2024-06-04-kl.-14.29.18.png',
    showInSlide: true,
    profileTitle: 'Teknisk rådgivning for bygg og anlegg',
    profileSummary:
      'Structor Tekniske Systemer arbeider med VVS, elektro, teknisk prosjektledelse, IKT, sikkerhet, energi og miljø.',
    profilePoints: [
      'Elektro og VVS-teknikk',
      'Teknisk prosjekt- og byggeledelse',
      'Energi, miljø, IKT og sikkerhet',
    ],
    accentColor: '#7aa331',
  },
  {
    name: 'Medvind Assistanse',
    floor: '3. etasje',
    logo: 'https://medvindassistanse.no/favicon-180.png',
    website: 'https://medvindassistanse.no/',
    category: 'Brukerstyrt personlig assistanse',
    profileImage:
      'https://pub-bb2e103a32db4e198524a2e9ed8f35b4.r2.dev/5b3905e3-b28a-4b48-a7e4-e87eaa0eaac2/id-preview-9e043643--dca4b923-1b40-4cd6-8f7f-02f174ae67d4.lovable.app-1782893189826.png',
    showInSlide: true,
    profileTitle: 'BPA på dine premisser',
    profileSummary:
      'Medvind Assistanse leverer brukerstyrt personlig assistanse med tett oppfølging, lokal tilstedeværelse og kvalitet i alle livets faser.',
    profilePoints: [
      'Brukerstyrt personlig assistanse',
      'Lokal og tett oppfølging',
      'ISO-sertifisert kvalitet og miljø',
    ],
    accentColor: '#f2a900',
  },
  { name: 'Akantus AS', floor: '3. etasje', logo: '', showInSlide: false },
  {
    name: 'Midt Norsk Industri Consult AS',
    floor: '3. etasje',
    logo: '',
    showInSlide: false,
  },

  // 4. ETASJE
  {
    name: 'HEV',
    floor: '4. etasje',
    logo: 'https://helgelandevent.no/wp-content/uploads/2019/09/HEV-logo.png',
    website: 'https://helgelandevent.no/',
    category: 'Event og profilering',
    bgImage: hevBg,
    profileImage: hevBg,
    showInSlide: true, // full slide
    title: 'Event og Profilering',
    description:
      'HEV startet som en høytsvevende idé av en gruppe venner i 2007. Siden da har vi vokst og utviklet oss til å bli en av de ledende aktørene i bransjen. Vi jobber i dag for å HEVe privat og offentlig næringsliv over hele landet, med Helgeland som kjerneområde.',
    profileTitle: 'Totalservice event- og profileringsbyrå',
    profileSummary:
      'HEV utvikler og gjennomfører eventer, profilering, design, branding, markedsføring og komplette produksjoner med Helgeland som kjerneområde.',
    profilePoints: [
      'Event og prosjektledelse',
      'Design, profil og markedsmateriell',
      'Lokalt forankret gjennomføring',
    ],
    accentColor: '#f45500',
    titleColor: '#f45500',
    descColor: '#fff',
    email: 'post@helgelandevent.no',
    phone: '(+47) 989 01 242',
    hours: 'Man - Fre, 08:00 - 16:00',
  },
];
